import { IS_DEV } from "@/utils/env";

/**
 * Runs `fn` once after first paint, when idle, on user interaction, when the tab becomes
 * visible, or after a timeout — whichever comes first. Used to defer GraphQL and similar
 * work off the critical path to first contentful paint / LCP.
 *
 * @param {() => void|Promise<void>} fn - Async-safe work unit; errors are logged in dev only.
 */
export function scheduleNonCriticalWorkOnce(fn) {
  if (typeof document === "undefined") {
    void Promise.resolve(fn()).catch((err) => {
      if (IS_DEV) console.error("[scheduleNonCriticalWorkOnce]", err);
    });
    return;
  }

  let done = false;
  let idleCallbackId = null;
  let hardTimeoutId = null;
  const hardTimeoutMs = 8000;

  const teardown = () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    document.removeEventListener("pointerdown", onInteract, listenerOpts);
    document.removeEventListener("keydown", onInteract, listenerOpts);
    if (idleCallbackId != null) {
      if (typeof globalThis.cancelIdleCallback === "function") {
        globalThis.cancelIdleCallback(idleCallbackId);
      } else {
        clearTimeout(idleCallbackId);
      }
      idleCallbackId = null;
    }
  };

  const run = () => {
    if (done) return;
    done = true;
    if (hardTimeoutId != null) clearTimeout(hardTimeoutId);
    teardown();
    void Promise.resolve(fn()).catch((err) => {
      if (IS_DEV) console.error("[scheduleNonCriticalWorkOnce]", err);
    });
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") run();
  };

  const listenerOpts = { capture: true, passive: true };
  const onInteract = () => run();

  const scheduleIdle = () => {
    if (done) return;
    const ric = globalThis.requestIdleCallback;
    if (typeof ric === "function") {
      idleCallbackId = ric(() => run(), { timeout: 4000 });
    } else {
      idleCallbackId = setTimeout(run, 1);
    }
  };

  const raf = globalThis.requestAnimationFrame || ((cb) => setTimeout(cb, 0));
  raf(() => raf(() => scheduleIdle()));

  document.addEventListener("visibilitychange", onVisibilityChange);
  document.addEventListener("pointerdown", onInteract, listenerOpts);
  document.addEventListener("keydown", onInteract, listenerOpts);

  hardTimeoutId = setTimeout(run, hardTimeoutMs);
}
